import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Save, Loader2 } from 'lucide-react';
import { UpgradePrompt } from '../Subscription/UpgradePrompt';

interface SaveButtonProps {
  className?: string;
  onSave?: () => void;
  disabled?: boolean; // allow parent to gray out explicitly
}

export const SaveButton: React.FC<SaveButtonProps> = ({ className = '', onSave, disabled }) => {
  const { user, subscriptionStatus } = useAuth();
  const [isUpgradePromptOpen, setIsUpgradePromptOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasActiveSubscription = subscriptionStatus?.has_active_subscription || false;

  const handleClick = async () => {
    if (!user) {
      // User not signed in - show upgrade prompt
      setIsUpgradePromptOpen(true);
      return;
    }

    if (!hasActiveSubscription) {
      setIsUpgradePromptOpen(true);
      return;
    }

    if (onSave) {
      try {
        setIsSaving(true);
        await onSave();
      } catch (error) {
        console.error('Failed to save analysis:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isSaving || disabled}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-green-300 text-green-700 bg-green-100 hover:border-green-400 hover:bg-green-50 ${className}`}
        title={
          !user
            ? 'Sign in to save analyses'
            : hasActiveSubscription
            ? 'Save current analysis'
            : 'Subscribe to save analyses'
        }
      >
        {isSaving ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Save className="w-5 h-5" />
        )}
        <span className="text-sm font-medium">
          {isSaving ? 'Saving...' : 'Save Analysis'}
        </span>
      </button>

      <UpgradePrompt
        isOpen={isUpgradePromptOpen}
        onClose={() => setIsUpgradePromptOpen(false)}
        requiresAuth={!user}
      />
    </>
  );
};
